"use client";

import { useMutation, useSuspenseQuery } from "@apollo/client";
import { useRef, useState } from "react";
import {
  type AppReviewRequestWorkCompletion,
  shouldShowAppReviewRequestDialog,
} from "@/components/model/notification/_function/appReviewRequestDialog";
import { shouldShowFirstWorkCompleteDialog } from "@/components/model/notification/_function/firstWorkCompleteDialog";
import { useFirstWorkCompleteDialog } from "@/components/model/notification/_function/useFirstWorkCompleteDialog";
import { AppReviewRequestDialog } from "@/components/model/notification/AppReviewRequestDialog";
import { FirstWorkCompleteDialog } from "@/components/model/notification/FirstWorkCompleteDialog";

import {
  displayPayrollNotificationsQuery,
  updatePayrollNotifyMutation,
} from "./_graphql";
import { PayrollConfirmModal } from "./PayrollConfirmModal";

export function PayrollServerUpdateModal() {
  const {
    data: { displayPayrollNotifications, temporaryJobWorker },
  } = useSuspenseQuery(displayPayrollNotificationsQuery);
  const workerId = temporaryJobWorker?.id;
  const workerStatus = temporaryJobWorker?.status;
  const { markAsSeen } = useFirstWorkCompleteDialog(workerId);
  const isFirstWorkCompletedRef = useRef(false);
  const [firstWorkCompleteDialogOpened, setFirstWorkCompleteDialogOpened] =
    useState(false);
  // キューを閉じ終えるまでの勤務完了をためておき、最後の 1 件を閉じたときにまとめて判定する
  const queuedWorkCompletionsRef = useRef<AppReviewRequestWorkCompletion[]>([]);
  const [appReviewRequestDialogOpened, setAppReviewRequestDialogOpened] =
    useState(false);
  const [mutation] = useMutation(updatePayrollNotifyMutation);
  // 閉じるたびに refetch され表示済みの通知が一覧から消えるため、位置ではなく通知 ID で閉じた状態を持つ
  const [closedNotifyIds, setClosedNotifyIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const notifyKeyOf = (index: number) =>
    displayPayrollNotifications[index]?.displayNotifyId ?? `index-${index}`;

  const closeModal = (notifyKey: string) => {
    setClosedNotifyIds((prev) => new Set(prev).add(notifyKey));
  };

  const updateModalStateAndServerState = async (index: number) => {
    const notification = displayPayrollNotifications[index];
    const notifyKey = notifyKeyOf(index);
    isFirstWorkCompletedRef.current = Boolean(
      notification?.isFirstWorkCompleted,
    );
    if (notification) {
      queuedWorkCompletionsRef.current.push({
        isWorkersCompensation: notification.isWorkersCompensation,
        isFirstActualWorkCompleted: notification.isFirstActualWorkCompleted,
      });
    }
    const isLastOpenedModal = displayPayrollNotifications.every(
      (_, i) => i === index || closedNotifyIds.has(notifyKeyOf(i)),
    );

    await mutation({
      variables: {
        input: {
          displayNotifyId:
            displayPayrollNotifications[index]?.displayNotifyId || "",
        },
      },
      onError: () => {},
      onCompleted: () => {
        closeModal(notifyKey);
      },
      refetchQueries: ["DisplayPayrollNotifications"],
    });
    closeModal(notifyKey);

    if (
      shouldShowFirstWorkCompleteDialog({
        isFirstWorkCompleted: isFirstWorkCompletedRef.current,
        workerId,
      })
    ) {
      setFirstWorkCompleteDialogOpened(true);
    }

    if (isLastOpenedModal) {
      const completions = queuedWorkCompletionsRef.current;
      queuedWorkCompletionsRef.current = [];
      if (
        shouldShowAppReviewRequestDialog({
          workerId,
          workerStatus,
          completions,
        })
      ) {
        setAppReviewRequestDialogOpened(true);
      }
    }
  };

  return (
    <>
      {displayPayrollNotifications.map((displayPayrollNotification, index) => (
        <PayrollConfirmModal
          finalPaymentAmount={
            displayPayrollNotification?.finalPaymentAmount || 0
          }
          handleSubmit={() => updateModalStateAndServerState(index)}
          opened={!closedNotifyIds.has(notifyKeyOf(index))}
          key={notifyKeyOf(index)}
          keySuffix={index.toString()}
          zIndexOffset={displayPayrollNotifications.length - index}
          shareToken={displayPayrollNotification?.shareToken}
        />
      ))}
      <FirstWorkCompleteDialog
        opened={firstWorkCompleteDialogOpened}
        onDismiss={() => setFirstWorkCompleteDialogOpened(false)}
        onNavigateAway={() => setFirstWorkCompleteDialogOpened(false)}
        onMarkSeen={markAsSeen}
      />
      <AppReviewRequestDialog
        opened={appReviewRequestDialogOpened}
        workerId={workerId}
        onClose={() => setAppReviewRequestDialogOpened(false)}
      />
    </>
  );
}
